import {
  coachProgressSummary,
  resetCoachProgress,
} from "../prototypes/proclaim/ui/Coach";
import {
  backupToJson,
  downloadBackupFile,
  exportProgress,
  importProgress,
  parseBackup,
  playerSaveAreasOnDevice,
  resetProgress,
  summarizeBackup,
} from "../shared/progress/backup";
import { WHATS_NEW } from "../shared/whatsNew";
import { openWhatsNewDialog } from "../shared/whatsNewDialog";

document.documentElement.dataset.app = "menu";

const statusEl = document.getElementById("settings-status");
const saveAreasEl = document.getElementById("settings-save-areas");
const backupMsg = document.getElementById("settings-backup-msg");
const resetMsg = document.getElementById("settings-reset-msg");
const coachStatusEl = document.getElementById("settings-coach-status");
const coachMsg = document.getElementById("settings-coach-msg");
const btnExport = document.getElementById("btn-export");
const inputImport = document.getElementById("input-import") as HTMLInputElement | null;
const pasteArea = document.getElementById("paste-import") as HTMLTextAreaElement | null;
const btnImportPaste = document.getElementById("btn-import-paste");
const btnReset = document.getElementById("btn-reset");
const btnResetCoach = document.getElementById(
  "btn-reset-coach",
) as HTMLButtonElement | null;

function setMsg(el: HTMLElement | null, text: string, isError = false): void {
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("error", isError);
}

function refreshCoachStatus(): void {
  const { seen, stepsDone, stepsTotal } = coachProgressSummary();
  if (coachStatusEl) {
    coachStatusEl.textContent = seen
      ? `Tips seen: ${stepsDone} of ${stepsTotal} (intro, first play + Answers, Abide/Prayers, Will drain, first defeat, Settled).`
      : "Tutorial tips will show automatically on your next fights.";
  }
  if (btnResetCoach) {
    btnResetCoach.disabled = !seen;
    btnResetCoach.title = seen
      ? "Show first-fight tips again on the next missions"
      : "Nothing to reset — tips already ready to show";
  }
}

/** How many newest notes show before “Show older”. */
const CHANGELOG_PREVIEW = 3;
let changelogExpanded = false;

function makeChangelogItem(
  entry: (typeof WHATS_NEW)[number],
  index: number,
): HTMLLIElement {
  const li = document.createElement("li");
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "settings-changelog-item";
  btn.setAttribute(
    "aria-label",
    entry.date
      ? `Read “${entry.title}” (${entry.date})`
      : `Read “${entry.title}”`,
  );

  const title = document.createElement("span");
  title.className = "settings-changelog-item-title";
  title.textContent = entry.title;

  const meta = document.createElement("span");
  meta.className = "settings-changelog-item-meta";
  const bits: string[] = [];
  if (entry.date) bits.push(entry.date);
  bits.push(
    `${entry.bullets.length} point${entry.bullets.length === 1 ? "" : "s"}`,
  );
  meta.textContent = bits.join(" · ");

  const chevron = document.createElement("span");
  chevron.className = "settings-changelog-item-go";
  chevron.setAttribute("aria-hidden", "true");
  chevron.textContent = "Read →";

  btn.append(title, meta, chevron);
  btn.addEventListener("click", () => {
    openWhatsNewDialog({
      entries: WHATS_NEW,
      startIndex: index,
      markCaughtUpOnClose: false,
      closeLabel: "Close",
    });
  });
  li.appendChild(btn);
  return li;
}

/** Browseable changelog — re-open any note after the home popup is dismissed. */
function renderChangelogList(): void {
  const list = document.getElementById("settings-changelog-list");
  if (!list) return;
  list.replaceChildren();

  if (!WHATS_NEW.length) {
    const empty = document.createElement("li");
    empty.className = "settings-changelog-empty";
    empty.textContent = "No update notes yet.";
    list.appendChild(empty);
    return;
  }

  const total = WHATS_NEW.length;
  const hasMore = total > CHANGELOG_PREVIEW;
  const showAll = changelogExpanded || !hasMore;
  const visible = showAll ? total : CHANGELOG_PREVIEW;

  for (let i = 0; i < visible; i++) {
    list.appendChild(makeChangelogItem(WHATS_NEW[i]!, i));
  }

  if (!hasMore) return;

  const olderCount = total - CHANGELOG_PREVIEW;
  const toggleLi = document.createElement("li");
  toggleLi.className = "settings-changelog-toggle-wrap";
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "settings-changelog-toggle";
  toggle.setAttribute("aria-expanded", showAll ? "true" : "false");
  if (showAll) {
    toggle.textContent = "Show fewer";
    toggle.title = `Hide older updates (show newest ${CHANGELOG_PREVIEW})`;
  } else {
    toggle.textContent = `Show ${olderCount} older update${olderCount === 1 ? "" : "s"}`;
    toggle.title = "Expand full changelog";
  }
  toggle.addEventListener("click", () => {
    changelogExpanded = !changelogExpanded;
    renderChangelogList();
    // Keep focus on the toggle after re-render
    document
      .querySelector<HTMLButtonElement>(".settings-changelog-toggle")
      ?.focus();
  });
  toggleLi.appendChild(toggle);
  list.appendChild(toggleLi);
}

function refreshStatus(): void {
  const areas = playerSaveAreasOnDevice();
  // Progress for players: campaign / collection / decks / endless (not audio prefs alone)
  const progressAreas = areas.filter((a) => a.id !== "audio");

  if (statusEl) {
    if (progressAreas.length === 0) {
      statusEl.textContent =
        "No saved progress yet — play campaign or endless to create data on this device.";
    } else {
      statusEl.textContent =
        "Progress is saved on this device. Export a backup before clearing browser data or switching browsers.";
    }
  }

  if (saveAreasEl) {
    saveAreasEl.replaceChildren();
    if (areas.length === 0) {
      saveAreasEl.hidden = true;
    } else {
      saveAreasEl.hidden = false;
      for (const area of areas) {
        const li = document.createElement("li");
        li.textContent = area.label;
        saveAreasEl.appendChild(li);
      }
    }
  }

  refreshCoachStatus();
  renderChangelogList();
}

function doExport(): void {
  try {
    const backup = exportProgress({ includeAudio: true });
    downloadBackupFile(backup);
    const n = Object.keys(backup.data).length;
    setMsg(
      backupMsg,
      `Downloaded backup (${n} item${n === 1 ? "" : "s"} · ${backup.exportedAt.slice(0, 19).replace("T", " ")} UTC).`,
      false,
    );
  } catch (e) {
    setMsg(
      backupMsg,
      e instanceof Error ? e.message : "Could not export.",
      true,
    );
  }
}

function describeBackupContents(summary: ReturnType<typeof summarizeBackup>): string {
  const parts: string[] = [];
  if (summary.hasCampaign) parts.push("campaign");
  if (summary.hasCollection) parts.push("collection");
  if (summary.hasDecks) parts.push("decks");
  if (summary.hasEndless) parts.push("endless");
  if (summary.keys.includes("sots-audio-v1")) parts.push("audio settings");
  return parts.length ? parts.join(", ") : "saved data";
}

function doImport(raw: string): void {
  try {
    const backup = parseBackup(raw);
    const summary = summarizeBackup(backup);
    const when = summary.exportedAt
      ? new Date(summary.exportedAt).toLocaleString()
      : "unknown date";
    const ok = window.confirm(
      `Restore backup from ${when}?\n\n` +
        `This replaces progress on this device with the file contents.\n` +
        `Includes: ${describeBackupContents(summary)}.\n\n` +
        `Export a backup first if you might need the current save.`,
    );
    if (!ok) {
      setMsg(backupMsg, "Restore cancelled.", false);
      return;
    }
    importProgress(backup);
    setMsg(
      backupMsg,
      "Progress restored. Reloading…",
      false,
    );
    window.setTimeout(() => {
      window.location.href = "/";
    }, 600);
  } catch (e) {
    setMsg(
      backupMsg,
      e instanceof Error ? e.message : "Could not restore backup.",
      true,
    );
  }
}

function doResetCoach(): void {
  setMsg(coachMsg, "", false);
  const { seen } = coachProgressSummary();
  if (!seen) {
    setMsg(
      coachMsg,
      "Tutorial tips are already ready — start a fight to see them.",
      false,
    );
    refreshCoachStatus();
    return;
  }
  const ok = window.confirm(
    "Replay tutorial tips?\n\n" +
      "The next fights will show the intro coach, first-play tip, Abide/Prayer tip, Will-drain tip, and longer first-defeat message again.\n\n" +
      "Campaign progress, collection, and decks are not changed.",
  );
  if (!ok) {
    setMsg(coachMsg, "Cancelled.", false);
    return;
  }
  try {
    resetCoachProgress();
    setMsg(
      coachMsg,
      "Tutorial tips reset. Open a campaign stage or free play to see them again. Pause → How to fight is always available.",
      false,
    );
    refreshCoachStatus();
  } catch (e) {
    setMsg(
      coachMsg,
      e instanceof Error ? e.message : "Could not reset tutorial tips.",
      true,
    );
  }
}

function doReset(): void {
  setMsg(resetMsg, "", false);
  const ok1 = window.confirm(
    "Reset ALL progress on this device?\n\n" +
      "Collection, campaign, decks, endless scores, packs, and tutorial tips will be wiped.\n" +
      "Audio settings are kept.\n\n" +
      "This cannot be undone without a backup file.",
  );
  if (!ok1) {
    setMsg(resetMsg, "Reset cancelled.", false);
    return;
  }
  const ok2 = window.confirm(
    "Final confirmation: erase progress on this device?",
  );
  if (!ok2) {
    setMsg(resetMsg, "Reset cancelled.", false);
    return;
  }
  try {
    resetProgress({ includeAudio: false });
    setMsg(resetMsg, "Progress cleared. Reloading…", false);
    window.setTimeout(() => {
      window.location.href = "/";
    }, 500);
  } catch (e) {
    setMsg(
      resetMsg,
      e instanceof Error ? e.message : "Could not reset.",
      true,
    );
  }
}

btnExport?.addEventListener("click", () => doExport());

inputImport?.addEventListener("change", () => {
  const file = inputImport.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    const text = typeof reader.result === "string" ? reader.result : "";
    doImport(text);
    inputImport.value = "";
  };
  reader.onerror = () => {
    setMsg(backupMsg, "Could not read that file.", true);
    inputImport.value = "";
  };
  reader.readAsText(file);
});

btnImportPaste?.addEventListener("click", () => {
  const text = pasteArea?.value?.trim() ?? "";
  if (!text) {
    setMsg(backupMsg, "Paste backup JSON first.", true);
    return;
  }
  doImport(text);
});

btnReset?.addEventListener("click", () => doReset());
btnResetCoach?.addEventListener("click", () => doResetCoach());

// Dev helper: expose for console
(window as unknown as { __sotsExport?: () => string }).__sotsExport = () =>
  backupToJson(exportProgress());

refreshStatus();
